import classNames from 'classnames';
import { ArrowRight, Check, ChevronRight, Clock3, LockKeyhole, Sparkles, Target } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActiveQuestDto } from 'shared/interfaces/quest/active-quest.dto';
import { QuestCandidateSetDto } from 'shared/interfaces/quest/quest-candidate-set.dto';
import { questApi } from 'src/api/quest/quest';
import { useQuestsContext } from 'src/contexts/QuestsContext/QuestsContext';
import { useTranslationByKey } from 'src/hooks/use-translation-by-key';

const QUEST_SLOT_COUNT = 3;

const DashboardCurrentQuests: React.FC = () => {
  const t = useTranslationByKey('PAGES.QUESTS');
  const navigate = useNavigate();
  const { activeQuests, hasLoadedQuests, isLoadingQuests, ensureQuestsLoaded } = useQuestsContext();
  const [candidateSets, setCandidateSets] = useState<QuestCandidateSetDto[]>([]);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(true);
  const activeQuestSignature = activeQuests.map((quest) => `${quest.id}:${quest.completedOn ?? ''}`).join('|');
  const previousActiveQuestSignatureRef = useRef(activeQuestSignature);

  const refreshCandidates = useCallback(async () => {
    setIsLoadingCandidates(true);
    try {
      const response = await questApi.getCandidates();
      setCandidateSets(response.sets);
    } catch {
      setCandidateSets([]);
    } finally {
      setIsLoadingCandidates(false);
    }
  }, []);

  useEffect(() => {
    void ensureQuestsLoaded().catch(() => undefined);
  }, [ensureQuestsLoaded]);

  useEffect(() => {
    void refreshCandidates();
  }, [refreshCandidates]);

  useEffect(() => {
    if (!hasLoadedQuests || previousActiveQuestSignatureRef.current === activeQuestSignature) return;

    previousActiveQuestSignatureRef.current = activeQuestSignature;
    void refreshCandidates();
  }, [activeQuestSignature, hasLoadedQuests, refreshCandidates]);

  const questSlots = useMemo(() => {
    const slots: Array<ActiveQuestDto | null> = Array<null>(QUEST_SLOT_COUNT).fill(null);
    const unassigned: ActiveQuestDto[] = [];

    activeQuests.forEach((quest) => {
      if (
        quest.slotNumber &&
        quest.slotNumber >= 1 &&
        quest.slotNumber <= QUEST_SLOT_COUNT &&
        !slots[quest.slotNumber - 1]
      ) {
        slots[quest.slotNumber - 1] = quest;
      } else {
        unassigned.push(quest);
      }
    });

    unassigned.forEach((quest) => {
      const emptyIndex = slots.findIndex((slot) => slot === null);
      if (emptyIndex >= 0) slots[emptyIndex] = quest;
    });

    return slots;
  }, [activeQuests]);

  const navigateToQuestAction = (quest: ActiveQuestDto) => {
    if (quest.completedOn || quest.expiredOn) {
      navigate(`/quests?questId=${quest.id}`);
      return;
    }

    navigate(
      quest.eligibleCategory === 'games' ? '/games' : quest.eligibleCategory === 'surveys' ? '/surveys' : '/home'
    );
  };

  const isLoading = (!hasLoadedQuests && isLoadingQuests) || isLoadingCandidates;

  return (
    <section aria-labelledby="dashboard-current-quests-title" className="w-full min-w-0">
      <div className="mb-2.5 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#eef1ff] text-[#3f54a4]">
            <Target className="h-3.5 w-3.5" />
          </div>
          <h2
            id="dashboard-current-quests-title"
            className="truncate font-zillaSlab text-lg font-semibold text-slate-900"
          >
            {t('QUEST_BOARD')}
          </h2>
        </div>
        <button
          type="button"
          onClick={() => navigate('/quests')}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1.5 font-manrope text-[10px] font-extrabold text-[#3f54a4] transition hover:bg-[#f4f6ff] focus:outline-none focus:ring-2 focus:ring-indigo-200"
        >
          {t('TITLE')}
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-2.5 md:grid-cols-3">
        {isLoading
          ? [0, 1, 2].map((slot) => (
              <div
                key={slot}
                className="h-[108px] animate-pulse rounded-xl border border-slate-200 bg-slate-100 md:h-[148px]"
              />
            ))
          : questSlots.map((quest, index) => {
              const candidateSet = candidateSets.find((set) => set.slotNumber === index + 1);

              if (!quest) {
                const hasChoices = Boolean(candidateSet);
                return (
                  <article
                    key={candidateSet ? `candidate-${candidateSet.id}` : `empty-${index}`}
                    className={classNames(
                      'flex min-h-[108px] min-w-0 items-center gap-3 rounded-xl border p-3 shadow-sm md:min-h-[148px] md:flex-col md:items-stretch',
                      hasChoices ? 'border-indigo-200 bg-indigo-50/60' : 'border-slate-200 bg-slate-50'
                    )}
                  >
                    <div className="flex min-w-0 flex-1 items-start gap-3 md:block">
                      <div
                        className={classNames(
                          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white ring-1 md:h-8 md:w-8',
                          hasChoices ? 'text-[#3f54a4] ring-indigo-200' : 'text-slate-400 ring-slate-200'
                        )}
                      >
                        {hasChoices ? <Sparkles className="h-4 w-4" /> : <LockKeyhole className="h-4 w-4" />}
                      </div>
                      <div className="min-w-0 md:mt-2">
                        <p
                          className={classNames(
                            'font-manrope text-[9px] font-extrabold uppercase tracking-[0.14em]',
                            hasChoices ? 'text-[#3f54a4]' : 'text-slate-400'
                          )}
                        >
                          {hasChoices
                            ? candidateSet?.recoveryReason
                              ? t('REPLACEMENT_READY_STATUS')
                              : t('CHOICE_READY_STATUS')
                            : t('LOCKED_STATUS')}
                        </p>
                        <h3 className="mt-0.5 truncate font-zillaSlab text-[15px] font-semibold text-slate-800">
                          {hasChoices ? t('CHOOSE_A_QUEST') : t('LOCKED_SLOT', { number: index + 1 })}
                        </h3>
                        <p className="mt-0.5 line-clamp-1 font-manrope text-[10px] leading-4 text-slate-500 md:line-clamp-2">
                          {hasChoices ? t('CHOICE_READY_BODY') : t('LOCKED_BODY')}
                        </p>
                      </div>
                    </div>
                    {hasChoices && (
                      <button
                        type="button"
                        onClick={() => navigate('/quests?focus=slots')}
                        className="inline-flex min-h-9 shrink-0 items-center justify-center gap-1 rounded-lg bg-[#3f54a4] px-3 font-manrope text-[10px] font-extrabold text-white transition hover:bg-[#344690] focus:outline-none focus:ring-2 focus:ring-indigo-200 md:w-full"
                      >
                        {candidateSet?.recoveryReason ? t('CHOOSE_REPLACEMENT') : t('CHOOSE')}
                        <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </article>
                );
              }

              const completed = Boolean(quest.completedOn);
              const expired = Boolean(quest.expiredOn) && !completed;
              const progress = Math.min(quest.progress, quest.needed);
              const progressPercent = Math.min(100, Math.round((progress / Math.max(1, quest.needed)) * 100));
              const actionLabel = completed
                ? t('CLAIM')
                : expired
                  ? t('CHOOSE_REPLACEMENT')
                  : quest.eligibleCategory === 'games'
                    ? t('GO_TO_GAMES')
                    : quest.eligibleCategory === 'surveys'
                      ? t('GO_TO_SURVEYS')
                      : t('START_EARNING');

              return (
                <article
                  key={quest.id}
                  className={classNames(
                    'flex min-h-[108px] min-w-0 items-center gap-3 rounded-xl border p-3 shadow-sm md:min-h-[148px] md:flex-col md:items-stretch',
                    completed
                      ? 'border-emerald-200 bg-emerald-50/60'
                      : expired
                        ? 'border-amber-200 bg-amber-50/60'
                        : 'border-slate-200 bg-white'
                  )}
                >
                  <div className="min-w-0 flex-1 md:w-full">
                    <div className="flex items-center justify-between gap-2">
                      <p
                        className={classNames(
                          'font-manrope text-[9px] font-extrabold uppercase tracking-[0.14em]',
                          completed ? 'text-emerald-700' : expired ? 'text-amber-700' : 'text-[#3f54a4]'
                        )}
                      >
                        {completed ? t('COMPLETE_STATUS') : expired ? t('EXPIRED_STATUS') : t('IN_PROGRESS_STATUS')}
                      </p>
                      <span className="shrink-0 font-manrope text-[9px] font-bold text-slate-500">
                        {String(index + 1).padStart(2, '0')} / {QUEST_SLOT_COUNT}
                      </span>
                    </div>
                    <h3 className="mt-1 line-clamp-1 font-zillaSlab text-[15px] font-semibold leading-5 text-slate-900 md:line-clamp-2">
                      {t('QUEST_SENTENCE', {
                        action: quest.action,
                        needed: String(quest.needed),
                        item: quest.item ?? t('ACTIVITIES'),
                      })}
                    </h3>
                    <div className="mt-2">
                      <div className="mb-1 flex items-center justify-between font-manrope text-[9px] font-bold text-slate-500">
                        <span>{t('PROGRESS')}</span>
                        <span className={completed ? 'text-emerald-700' : 'text-slate-700'}>
                          {progress.toLocaleString()} / {quest.needed.toLocaleString()}
                        </span>
                      </div>
                      <div className="h-1.5 overflow-hidden rounded-full bg-slate-100 ring-1 ring-slate-200">
                        <div
                          className={classNames(
                            'h-full rounded-full transition-all',
                            completed
                              ? 'bg-gradient-to-r from-emerald-500 to-lime-400'
                              : expired
                                ? 'bg-amber-400'
                                : 'bg-gradient-to-r from-[#3f54a4] to-blue-400'
                          )}
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigateToQuestAction(quest)}
                    className={classNames(
                      'inline-flex min-h-9 shrink-0 items-center justify-center gap-1 rounded-lg px-3 font-manrope text-[10px] font-extrabold transition focus:outline-none focus:ring-2 focus:ring-indigo-200 md:w-full',
                      completed
                        ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                        : expired
                          ? 'bg-amber-500 text-white hover:bg-amber-600'
                          : 'bg-indigo-50 text-[#3f54a4] ring-1 ring-indigo-200 hover:bg-indigo-100'
                    )}
                  >
                    {completed ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : expired ? (
                      <Clock3 className="h-3.5 w-3.5" />
                    ) : null}
                    {actionLabel}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </article>
              );
            })}
      </div>
    </section>
  );
};

export default DashboardCurrentQuests;
